import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiFlag, FiUserX } from 'react-icons/fi';
import { IoMdMore } from 'react-icons/io';
import { knowledgeApi, messagingApi } from '../../support/supportApi';
import { AuthorFollowFeedButton } from '../../support/openDiscussions/components/feed/AuthorFollowFeedButton';
import { DiscussionReportModal } from '../../support/openDiscussions/components/moderation/DiscussionReportModal';
import MessagingComposeEditor from './MessagingComposeEditor';
import MessagingThreadMessage from './MessagingThreadMessage';
import { normalizeMessagingUser, preserveMessagingProfileMode, formatMessagingHandle } from './messagingUserUtils';
import { useMessagingPeerFollow } from './useMessagingPeerFollow';
import CenterModalWindow from '../../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import ButtonPill from '../../../layout/ReusableComponent/Buttons/button-pills';
import GroupedButton from '../../../layout/ReusableComponent/Buttons/GroupedButton';
import { useNotification } from '../../../layout/ReusableComponent/PopupNotification/PopupNotification';

import { applyProfileAvatarImgFallback } from '../profilePictureUrl';
const MESSAGE_POLL_MS = 5000;
const MESSAGE_PAGE_SIZE = 40;
const LOAD_MORE_TOP_THRESHOLD = 72;
const SCROLL_BOTTOM_THRESHOLD = 48;

function ThreadSkeleton() {
  return (
    <div className="messaging-thread__skeleton" aria-busy="true" aria-label="Loading messages">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className={`messaging-thread__skeleton-bubble${i % 2 ? ' is-mine' : ' is-theirs'}`}
        />
      ))}
    </div>
  );
}

function sortMessagesChronologically(list = []) {
  return [...list].sort(
    (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
  );
}

function mergeMessageLists(existing = [], incoming = []) {
  const byId = new Map(existing.map((m) => [m.messageId, m]));
  incoming.forEach((m) => {
    if (m?.messageId) byId.set(m.messageId, m);
  });
  return sortMessagesChronologically(Array.from(byId.values()));
}

/**
 * Right column: conversation thread with profile actions and composer.
 */
export default function MessagingThreadPanel({
  conversationId,
  otherUser,
  viewerUser = null,
  viewerUserId = '',
  opening = false,
  pinnedLayout = false,
  onBack,
  onViewPeerProfile,
  standalone = false,
  onConversationCreated,
  onConversationRead,
  onConversationUpdated,
}) {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const normalizedOther = normalizeMessagingUser(otherUser);
  const [resolvedOther, setResolvedOther] = useState(normalizedOther);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [resolvedConversationId, setResolvedConversationId] = useState('');
  const [sending, setSending] = useState(false);
  const [messageActionId, setMessageActionId] = useState('');
  const [deleteMessageId, setDeleteMessageId] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [error, setError] = useState('');
  const [editorKey, setEditorKey] = useState(0);
  const scrollRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const shouldScrollToBottomRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const scrollPreserveRef = useRef(null);
  const onConversationReadRef = useRef(onConversationRead);
  const onConversationCreatedRef = useRef(onConversationCreated);

  const displayOther = resolvedOther || normalizedOther;
  const peerId = String(
    displayOther?.userId || normalizedOther?.userId || otherUser?.userId || ''
  ).trim();
  const recipientProfileMode = displayOther?.profileMode || normalizedOther?.profileMode || 'personal';
  const normalizedViewer = normalizeMessagingUser(
    viewerUser || (viewerUserId ? { userId: viewerUserId } : null)
  );

  useEffect(() => {
    onConversationReadRef.current = onConversationRead;
    onConversationCreatedRef.current = onConversationCreated;
  }, [onConversationRead, onConversationCreated]);

  useEffect(() => {
    if (!peerId) return;
    setResolvedOther((prev) => preserveMessagingProfileMode(otherUser, prev));
  }, [peerId, otherUser]);

  useEffect(() => {
    setMessages([]);
    setHasMoreOlder(false);
    setResolvedConversationId('');
    setError('');
    isAtBottomRef.current = true;
    shouldScrollToBottomRef.current = true;
  }, [peerId, conversationId]);

  const { followState, busy: followBusy, follow, unfollow } = useMessagingPeerFollow(
    peerId,
    viewerUserId
  );

  const markConversationRead = useCallback(async (convId) => {
    const id = String(convId || '').trim();
    if (!id) return;
    try {
      await messagingApi.markRead({ conversationId: id });
      onConversationReadRef.current?.(id);
    } catch {
      /* ignore */
    }
  }, []);

  const resolveConversationId = useCallback(async () => {
    let convId = String(conversationId || resolvedConversationId || '').trim();
    let statusPayload = null;

    if (!convId && peerId) {
      const statusRes = await messagingApi.status({
        userId: peerId,
        recipientProfileMode,
      });
      if (statusRes?.success) {
        statusPayload = statusRes.status || null;
        convId = String(statusPayload?.conversationId || '').trim();
      }
    }

    return { convId, statusPayload };
  }, [conversationId, resolvedConversationId, peerId, recipientProfileMode]);

  const fetchInitialMessages = useCallback(async () => {
    if (!peerId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { convId, statusPayload } = await resolveConversationId();
      if (statusPayload) setStatus(statusPayload);

      const hasMessages =
        Boolean(convId) &&
        (conversationId ||
          statusPayload?.hasConversation ||
          (statusPayload?.messageCount || 0) > 0);

      if (!hasMessages) {
        setMessages([]);
        setHasMoreOlder(false);
        setResolvedConversationId('');
        return;
      }

      const data = await messagingApi.messages({ conversationId: convId, limit: MESSAGE_PAGE_SIZE });
      if (!data?.success) {
        setMessages([]);
        setHasMoreOlder(false);
        return;
      }

      const nextMessages = data.messages || [];
      setMessages(nextMessages);
      setHasMoreOlder(Boolean(data.hasMore));
      setResolvedConversationId(convId);
      setStatus(data.status || statusPayload);
      if (data.otherUser) {
        setResolvedOther((prev) => preserveMessagingProfileMode(data.otherUser, prev));
      }

      const hasUnreadForViewer = nextMessages.some(
        (m) => !m.isMine && !m.readAt && !m.isDeleted
      );
      if (hasUnreadForViewer) {
        void markConversationRead(convId);
      }

      shouldScrollToBottomRef.current = true;
      isAtBottomRef.current = true;
    } catch (err) {
      setError(err.message || 'Failed to load messages');
      setMessages([]);
      setHasMoreOlder(false);
    } finally {
      setLoading(false);
    }
  }, [peerId, conversationId, resolveConversationId, markConversationRead]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlderRef.current || !hasMoreOlder || !messages.length) return;

    const convId = String(resolvedConversationId || conversationId || '').trim();
    if (!convId) return;

    const oldest = messages[0];
    const before = oldest?.createdAt;
    if (!before) return;

    loadingOlderRef.current = true;
    setLoadingOlder(true);

    const el = scrollRef.current;
    if (el) {
      scrollPreserveRef.current = {
        scrollHeight: el.scrollHeight,
        scrollTop: el.scrollTop,
      };
    }

    try {
      const data = await messagingApi.messages({
        conversationId: convId,
        before,
        limit: MESSAGE_PAGE_SIZE,
      });
      if (!data?.success) return;

      const older = data.messages || [];
      if (older.length) {
        setMessages((prev) => mergeMessageLists(older, prev));
      }
      setHasMoreOlder(Boolean(data.hasMore));
    } catch {
      /* ignore load-more errors */
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  }, [hasMoreOlder, messages, resolvedConversationId, conversationId]);

  const pollMessages = useCallback(async () => {
    if (!peerId || loading) return;

    try {
      const convId = String(resolvedConversationId || conversationId || '').trim();
      if (!convId) return;

      const data = await messagingApi.messages({ conversationId: convId, limit: MESSAGE_PAGE_SIZE });
      if (!data?.success) return;

      const incoming = data.messages || [];
      setMessages((prev) => {
        const merged = mergeMessageLists(prev, incoming);
        const addedCount = merged.length - prev.length;
        if (addedCount > 0 && isAtBottomRef.current) {
          shouldScrollToBottomRef.current = true;
        }
        return merged;
      });
      if (!hasMoreOlder) {
        setHasMoreOlder(Boolean(data.hasMore));
      }
      if (data.status) setStatus(data.status);
    } catch {
      /* ignore silent poll errors */
    }
  }, [peerId, loading, resolvedConversationId, conversationId, hasMoreOlder]);

  useEffect(() => {
    void fetchInitialMessages();
  }, [fetchInitialMessages]);

  useEffect(() => {
    if (!peerId) return undefined;

    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      void pollMessages();
    };

    const intervalId = window.setInterval(poll, MESSAGE_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [peerId, pollMessages]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const preserve = scrollPreserveRef.current;
    if (preserve) {
      const heightDelta = el.scrollHeight - preserve.scrollHeight;
      el.scrollTop = preserve.scrollTop + heightDelta;
      scrollPreserveRef.current = null;
      return;
    }

    if (shouldScrollToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      shouldScrollToBottomRef.current = false;
    }
  }, [messages, loading, loadingOlder]);

  const handleMessagesScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD;

    if (el.scrollTop <= LOAD_MORE_TOP_THRESHOLD) {
      void loadOlderMessages();
    }
  }, [loadOlderMessages]);

  const handleSend = async (body) => {
    const text = String(body || '').trim();
    if (!text || sending || !peerId) return;
    setSending(true);
    setError('');
    try {
      const data = await messagingApi.send({
        recipientUserId: peerId,
        recipientProfileMode,
        body: text,
      });
      if (data?.success) {
        setMessages((prev) => mergeMessageLists(prev, [data.message]));
        setStatus(data.status || null);
        setEditorKey((k) => k + 1);
        shouldScrollToBottomRef.current = true;
        isAtBottomRef.current = true;
        if (!conversationId && !resolvedConversationId && data.message?.conversationId) {
          setResolvedConversationId(data.message.conversationId);
          onConversationCreatedRef.current?.(data.message.conversationId);
        }
        onConversationUpdated?.();
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleEditMessage = async (messageId, body) => {
    setMessageActionId(messageId);
    setError('');
    try {
      const data = await messagingApi.edit({ messageId, body });
      if (data?.success) {
        setMessages((prev) =>
          prev.map((m) => (m.messageId === messageId ? { ...m, ...data.message } : m))
        );
        onConversationUpdated?.();
        return true;
      }
      return false;
    } catch (err) {
      setError(err.message || 'Failed to edit message');
      return false;
    } finally {
      setMessageActionId('');
    }
  };

  const handleDeleteMessage = (messageId) => {
    const id = String(messageId || '').trim();
    if (!id) return;
    setDeleteMessageId(id);
  };

  const closeDeleteModal = () => {
    if (deleteBusy) return;
    setDeleteMessageId('');
  };

  const confirmDeleteMessage = async () => {
    const messageId = String(deleteMessageId || '').trim();
    if (!messageId || deleteBusy) return;
    setDeleteBusy(true);
    setMessageActionId(messageId);
    setError('');
    try {
      const data = await messagingApi.delete({ messageId });
      if (data?.success) {
        setMessages((prev) =>
          prev.map((m) => (m.messageId === messageId ? { ...m, ...data.message } : m))
        );
        onConversationUpdated?.();
        setDeleteMessageId('');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete message');
    } finally {
      setDeleteBusy(false);
      setMessageActionId('');
    }
  };

  const deletePreview = deleteMessageId
    ? String(messages.find((m) => m.messageId === deleteMessageId)?.body || '').trim()
    : '';

  const openProfile = useCallback(() => {
    if (standalone || !peerId) return;
    const mode =
      String(displayOther?.profileMode || 'personal').trim().toLowerCase() === 'social'
        ? 'social'
        : 'personal';
    const profileTarget = {
      userId: peerId,
      profileMode: mode,
      handle: String(displayOther?.handle || '').trim(),
    };
    if (typeof onViewPeerProfile === 'function') {
      onViewPeerProfile(profileTarget);
      return;
    }
    navigate(
      `/posts?view=user-profile&userId=${encodeURIComponent(peerId)}&profileMode=${encodeURIComponent(mode)}`
    );
  }, [displayOther?.handle, displayOther?.profileMode, navigate, onViewPeerProfile, peerId, standalone]);

  const submitPeerReport = useCallback(
    async (brief) => {
      if (!peerId) return;
      await knowledgeApi.discussionsReportSubmit({
        targetType: 'user',
        targetUserId: peerId,
        brief,
      });
      addNotification({
        theme: 'green',
        headingText: 'Report sent',
        message: 'Moderators will review it.',
      });
    },
    [addNotification, peerId]
  );

  const confirmBlockPeer = useCallback(async () => {
    if (!peerId || blockBusy) return;
    setBlockBusy(true);
    try {
      await messagingApi.block({ userId: peerId });
      if (followState?.status === 'active' || followState?.status === 'pending') {
        await unfollow();
      }
      addNotification({
        theme: 'green',
        headingText: 'Blocked',
        message: `${displayOther?.displayName || 'This user'} will no longer be able to message you.`,
      });
      setBlockOpen(false);
      onBack?.();
    } catch (err) {
      addNotification({
        theme: 'red',
        headingText: 'Block',
        message: err?.message || 'Could not block this user.',
      });
    } finally {
      setBlockBusy(false);
    }
  }, [
    addNotification,
    blockBusy,
    displayOther?.displayName,
    followState?.status,
    onBack,
    peerId,
    unfollow,
  ]);

  const peerMenuItems = useMemo(() => {
    if (!peerId || followState?.status === 'self') return [];
    const items = [
      ...(standalone
        ? []
        : [
            {
              id: 'view-profile',
              text: 'View profile',
              icon: <FiExternalLink aria-hidden />,
              onClick: openProfile,
            },
          ]),
      {
        id: 'block',
        text: 'Block',
        icon: <FiUserX aria-hidden />,
        onClick: () => setBlockOpen(true),
      },
      {
        id: 'report',
        text: 'Report',
        icon: <FiFlag aria-hidden />,
        onClick: () => setReportOpen(true),
      },
    ];
    return items;
  }, [followState?.status, openProfile, peerId, standalone]);

  const canSend = status?.canSend !== false;
  const limitHint =
    status && !status.mutualFollowers && status.messagesRemaining != null
      ? `${status.messagesRemaining} message${status.messagesRemaining === 1 ? '' : 's'} left before you follow each other`
      : '';

  return (
    <div
      className={`messaging-thread${opening ? ' is-opening' : ''}${
        pinnedLayout ? ' messaging-thread--mobile-pinned' : ''
      }`}
    >
      <header className="messaging-thread__header">
        {onBack ? (
          <button
            type="button"
            className="messaging-thread__back noOutline-text"
            onClick={onBack}
            aria-label="Back to conversations"
          >
            <FiArrowLeft aria-hidden />
          </button>
        ) : null}
        <div className="messaging-thread__header-main">
          {standalone ? (
            <span className="messaging-thread__avatar-btn messaging-thread__avatar-btn--static" aria-hidden>
              <img
                src={displayOther?.profilePictureUrl}
                alt=""
                className="messaging-thread__avatar"
                onError={(e) => {
                  applyProfileAvatarImgFallback(e.currentTarget, {
                    userId: displayOther?.userId,
                    name: displayOther?.displayName,
                  });
                }}
              />
            </span>
          ) : (
            <button
              type="button"
              className="messaging-thread__avatar-btn noOutline-text"
              onClick={openProfile}
              aria-label={`Open ${displayOther?.displayName || 'user'} profile`}
            >
              <img
                src={displayOther?.profilePictureUrl}
                alt=""
                className="messaging-thread__avatar"
                onError={(e) => {
                  applyProfileAvatarImgFallback(e.currentTarget, {
                    userId: displayOther?.userId,
                    name: displayOther?.displayName,
                  });
                }}
              />
            </button>
          )}
          <div className="messaging-thread__identity">
            <div className="messaging-thread__title-row">
              {standalone ? (
                <span className="messaging-thread__name-btn messaging-thread__name-btn--static">
                  {displayOther?.displayName || 'User'}
                </span>
              ) : (
                <button
                  type="button"
                  className="messaging-thread__name-btn noOutline-text"
                  onClick={openProfile}
                >
                  {displayOther?.displayName || 'User'}
                </button>
              )}
              <AuthorFollowFeedButton
                followState={followState}
                loading={followBusy}
                onFollow={follow}
                onUnfollow={unfollow}
                onCancelRequest={unfollow}
                className="messaging-thread__follow-inline"
              />
            </div>
            {displayOther?.handle ? (
              <span className="messaging-thread__handle">{formatMessagingHandle(displayOther.handle)}</span>
            ) : null}
            {status?.mutualFollowers ? (
              <span className="messaging-thread__badge">Mutual followers</span>
            ) : limitHint ? (
              <span className="messaging-thread__badge messaging-thread__badge--warn">{limitHint}</span>
            ) : null}
          </div>
        </div>
        {peerMenuItems.length ? (
          <GroupedButton
            className="messaging-thread__more"
            align="right"
            ariaLabel="Conversation options"
            triggerIcon={<IoMdMore aria-hidden />}
            triggerText=""
            items={peerMenuItems}
          />
        ) : null}
      </header>

      <div
        className="messaging-thread__messages-scroll"
        ref={scrollRef}
        onScroll={handleMessagesScroll}
      >
        <div className="messaging-thread__messages">
          {loadingOlder ? (
            <p className="messaging-thread__load-more" aria-live="polite">
              Loading earlier messages…
            </p>
          ) : null}
          {loading && !messages.length ? <ThreadSkeleton /> : null}
          {!loading && !messages.length ? (
            <div className="messaging-thread__intro">
              <p>
                This is the beginning of your conversation with{' '}
                <strong>{displayOther?.displayName || 'this person'}</strong>.
              </p>
            </div>
          ) : null}
          {messages.length
            ? messages.map((m) => (
                <MessagingThreadMessage
                  key={m.messageId}
                  message={m}
                  viewerUser={normalizedViewer}
                  otherUser={displayOther}
                  busy={messageActionId === m.messageId}
                  onEdit={handleEditMessage}
                  onDelete={handleDeleteMessage}
                />
              ))
            : null}
          {error ? <p className="messaging-thread__error messaging-thread__error--inline">{error}</p> : null}
        </div>
      </div>

      <MessagingComposeEditor
        editorKey={editorKey}
        canSend={canSend}
        sending={sending}
        onSend={handleSend}
        hideToolbar
        hideHint
        inlineRowLayout={standalone}
      />

      <DiscussionReportModal
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        title={`Report ${displayOther?.displayName || 'user'}`}
        description="Tell moderators what happened. Reports are reviewed by our safety team."
        submitLabel="Submit report"
        onSubmit={submitPeerReport}
      />

      <CenterModalWindow
        isOpen={blockOpen}
        onClose={() => {
          if (!blockBusy) setBlockOpen(false);
        }}
        width="min(420px, calc(100vw - 32px))"
        height="auto"
        contentStyle={{ height: 'auto' }}
        contentClassName="messaging-delete-modal"
      >
        <div className="messaging-delete-modal__body">
          <header className="messaging-delete-modal__head">
            <p className="messaging-delete-modal__eyebrow">Messages</p>
            <h3 className="messaging-delete-modal__title">
              Block {displayOther?.displayName || 'this user'}?
            </h3>
            <p className="messaging-delete-modal__lead">
              They will not be able to send you new messages. You can still view past messages unless you delete
              the conversation.
            </p>
          </header>
          <footer className="messaging-delete-modal__actions">
            <ButtonPill
              type="button"
              variant="outline"
              shape="pill"
              size="sm"
              color="brand"
              onClick={() => setBlockOpen(false)}
              disabled={blockBusy}
            >
              Cancel
            </ButtonPill>
            <ButtonPill
              type="button"
              variant="solid"
              shape="pill"
              size="sm"
              color="danger"
              onClick={confirmBlockPeer}
              disabled={blockBusy}
            >
              {blockBusy ? 'Blocking…' : 'Block user'}
            </ButtonPill>
          </footer>
        </div>
      </CenterModalWindow>

      <CenterModalWindow
        isOpen={Boolean(deleteMessageId)}
        onClose={closeDeleteModal}
        width="min(420px, calc(100vw - 32px))"
        height="auto"
        contentStyle={{ height: 'auto' }}
        contentClassName="messaging-delete-modal"
      >
        <div className="messaging-delete-modal__body">
          <header className="messaging-delete-modal__head">
            <p className="messaging-delete-modal__eyebrow">Messages</p>
            <h3 className="messaging-delete-modal__title">Delete message?</h3>
            <p className="messaging-delete-modal__lead">
              This message will be removed from the conversation for everyone. This cannot be undone.
            </p>
            {deletePreview ? (
              <blockquote className="messaging-delete-modal__preview">{deletePreview}</blockquote>
            ) : null}
          </header>
          <footer className="messaging-delete-modal__actions">
            <ButtonPill
              type="button"
              variant="outline"
              shape="pill"
              size="sm"
              color="brand"
              onClick={closeDeleteModal}
              disabled={deleteBusy}
            >
              Cancel
            </ButtonPill>
            <ButtonPill
              type="button"
              variant="solid"
              shape="pill"
              size="sm"
              color="danger"
              onClick={confirmDeleteMessage}
              disabled={deleteBusy}
            >
              {deleteBusy ? 'Deleting…' : 'Delete message'}
            </ButtonPill>
          </footer>
        </div>
      </CenterModalWindow>
    </div>
  );
}
